import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import PaymentScheduleStyle from './styles';
import { Form, Input, Button, Space, Flex } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { quoteDetailSelector } from 'src/redux/selectors/quoteDetailSelector';
import { toggleEditQuoteSelector } from 'src/redux/selectors/toggleEditQuoteSelector';
import { updateParamsQuoteDetail } from 'src/redux/actions/paramsQuoteDetailAction';
import { paramsQuoteDetailSelector } from 'src/redux/selectors/paramsQuoteDetailSelector';
import useQuoteTemplate from 'src/hooks/useQuoteTemplate';
import { formatCurrency } from 'src/const/contain';
import { DATE_FORMAT } from 'src/const/common';

const isValidDate = (date?: string) =>
    !!date && moment(date, DATE_FORMAT, true).isValid();

const formatAmount = (amount?: string) => `£${formatCurrency(amount || 0)}`;

const PaymentSchedule: React.FC = () => {
    const [form] = Form.useForm();

    const dispatch = useDispatch();
    const {
        paymentSchedule,
        price = {},
    } = useSelector(quoteDetailSelector);
    const isEdit = useSelector(toggleEditQuoteSelector);
    const { isRowTemplate } = useQuoteTemplate();
    const params = useSelector(paramsQuoteDetailSelector);

    const paymentScheduleValue = Form.useWatch('paymentSchedule', form);

    // ROW quotes: schedule comes from reservation prices, agents can't edit it
    const rowPaymentSchedule = useMemo(() => {
        const { totalDeposit, dueDate, deposit2, deposit3 } = price;
        const items = [{ key: formatAmount(totalDeposit), value: 'Due Now' }];
        [deposit2, deposit3].forEach((deposit) => {
            if (Number(deposit?.amount) > 0 && isValidDate(deposit?.dueDate)) {
                items.push({
                    key: formatAmount(deposit.amount),
                    value: deposit.dueDate,
                });
            }
        });
        items.push({ key: 'Balance Due Date', value: dueDate });
        return items;
    }, [price]);

    const displaySchedule = isRowTemplate
        ? rowPaymentSchedule
        : paymentSchedule;

    useEffect(() => {
        if (isRowTemplate) return;
        form.setFieldsValue({
            paymentSchedule: params?.paymentSchedule ??
                paymentSchedule ?? [{ key: '', value: '' }],
        });
        form.validateFields();
    }, [paymentSchedule, form, params?.paymentSchedule, isRowTemplate]);

    useEffect(() => {
        if (
            !isRowTemplate &&
            paymentScheduleValue &&
            JSON.stringify(paymentSchedule) !==
                JSON.stringify(paymentScheduleValue)
        ) {
            dispatch(
                updateParamsQuoteDetail({
                    paymentSchedule: paymentScheduleValue,
                }),
            );
        }
    }, [paymentScheduleValue, dispatch, paymentSchedule, isRowTemplate]);

    const renderContent = () => {
        if (isEdit && !isRowTemplate) {
            return (
                <Form form={form} layout="vertical">
                    <Form.List name="paymentSchedule">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space
                                        key={key}
                                        style={{
                                            display: 'flex',
                                        }}
                                        align="start"
                                    >
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'key']}
                                            label={null}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: '',
                                                },
                                            ]}
                                        >
                                            <Input placeholder="Enter Amount" />
                                        </Form.Item>

                                        <Form.Item
                                            {...restField}
                                            name={[name, 'value']}
                                            label={null}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: '',
                                                },
                                            ]}
                                        >
                                            <Input placeholder="Enter Date" />
                                        </Form.Item>

                                        {fields.length > 1 && (
                                            <MinusCircleOutlined
                                                style={{ marginTop: 8 }}
                                                onClick={() => remove(name)}
                                            />
                                        )}
                                    </Space>
                                ))}

                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                        className="btn-add"
                                    >
                                        Add
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form>
            );
        }
        return (
            <div className="box-values">
                {displaySchedule?.map((item: any, index: number) => (
                    <Flex
                        key={index}
                        className={
                            index + 1 === displaySchedule.length ? 'bold' : ''
                        }
                        justify="space-between"
                        gap={8}
                    >
                        <div>{item?.key}</div>
                        <div className="text-right">{item?.value}</div>
                    </Flex>
                ))}
            </div>
        );
    };

    return (
        <PaymentScheduleStyle className="card">
            <div className="header">Your Payment Schedule</div>
            {renderContent()}
        </PaymentScheduleStyle>
    );
};

export default PaymentSchedule;
